import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useCardGridData = <T extends { id: string }>({
  fetchPage,
  resetKey,
  initialPageCount = 0,
  optimisticRemovedIds,
}: {
  fetchPage: (parameters: { page: number; perPage: number }) => Promise<{ items: T[]; total: number }>;
  resetKey: string;
  initialPageCount?: number;
  optimisticRemovedIds?: string[];
}) => {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const currentPageReference = useRef(0);
  const generationReference = useRef(0);
  const isFetchingReference = useRef(false);
  const lastProcessedKeyReference = useRef<string | undefined>(undefined);

  const fetchPageReference = useRef(fetchPage);
  fetchPageReference.current = fetchPage;
  const initialPageCountReference = useRef(initialPageCount);
  initialPageCountReference.current = initialPageCount;

  const fetchNextPage = useCallback(async () => {
    if (isFetchingReference.current) return;
    isFetchingReference.current = true;

    const generation = generationReference.current;
    const nextPage = currentPageReference.current + 1;
    const isFirstPage = nextPage === 1;

    if (!isFirstPage) {
      setIsFetchingNextPage(true);
    }

    try {
      const pageCount = initialPageCountReference.current;
      if (isFirstPage && pageCount > 1) {
        const pagePromises = Array.from({ length: pageCount }, (_, index) =>
          fetchPageReference.current({ page: index + 1, perPage: DATA_GRID_LAZY_PAGE_SIZE })
        );
        const results = await Promise.all(pagePromises);

        if (generationReference.current !== generation) return;

        currentPageReference.current = pageCount;
        setTotal(results.at(-1)?.total ?? 0);
        setItems(results.flatMap(r => r.items));
      } else {
        const result = await fetchPageReference.current({ page: nextPage, perPage: DATA_GRID_LAZY_PAGE_SIZE });

        if (generationReference.current !== generation) return;

        currentPageReference.current = nextPage;
        setTotal(result.total);
        setItems(previous => (isFirstPage ? result.items : [...previous, ...result.items]));
      }
    } finally {
      if (generationReference.current === generation) {
        isFetchingReference.current = false;
        setIsLoading(false);
        setIsFetchingNextPage(false);
      }
    }
  }, []);

  const reset = () => {
    generationReference.current += 1;
    isFetchingReference.current = false;
    currentPageReference.current = 0;
    setItems([]);
    setTotal(0);
    setIsLoading(true);
  };

  const effectiveItems = useMemo(
    () => (optimisticRemovedIds?.length ? items.filter(item => !optimisticRemovedIds.includes(item.id)) : items),
    [items, optimisticRemovedIds]
  );
  const effectiveTotal = total - (optimisticRemovedIds?.length ?? 0);
  const hasNextPage =
    effectiveItems.length < effectiveTotal || (effectiveItems.length === 0 && currentPageReference.current === 0);

  useEffect(() => {
    if (lastProcessedKeyReference.current !== resetKey) {
      lastProcessedKeyReference.current = resetKey;
      generationReference.current += 1;
      isFetchingReference.current = false;
      currentPageReference.current = 0;
      setIsLoading(true);
      setIsFetchingNextPage(false);
    }

    if (currentPageReference.current === 0 && !isFetchingReference.current) {
      void fetchNextPage();
    }
  }, [resetKey]);

  return {
    items: effectiveItems,
    total: effectiveTotal,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    reset,
    pageCount: currentPageReference.current,
  };
};
